import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { PushNotifications } from '@capacitor/push-notifications';
import { Camera } from '@capacitor/camera';
import { Filesystem } from '@capacitor/filesystem';
import { logNativeEvent } from './nativeDiagnostics';

const isNative = () => Capacitor.isNativePlatform();
const isPushEnabled = () => String((import.meta as any).env?.VITE_ENABLE_PUSH_NOTIFICATIONS || '').toLowerCase() === 'true';

export type PermissionResolution = {
  granted: boolean;
  status: 'granted' | 'denied' | 'not-required' | 'error';
  message: string;
};

export async function initializeNativeExperience() {
  const resolvePlatformWithRetry = async () => {
    for (let attempt = 0; attempt < 6; attempt += 1) {
      try {
        if (Capacitor.isNativePlatform()) {
          const p = Capacitor.getPlatform();
          if (p && p !== 'web') return p;
        }
      } catch {
        // keep retrying
      }
      try {
        const bridge = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string } }).Capacitor;
        if (bridge?.isNativePlatform?.()) {
          const p = String(bridge.getPlatform?.() || '').toLowerCase();
          if (p && p !== 'web') return p;
        }
      } catch {
        // ignore bridge access errors during startup
      }
      await new Promise<void>((resolve) => window.setTimeout(resolve, 120 * (attempt + 1)));
    }
    return '';
  };

  const platform = await resolvePlatformWithRetry();
  if (!platform) {
    return;
  }
  document.documentElement.classList.add('native-runtime', `native-${platform}`);
  initializeNativeViewportHandling();
  logNativeEvent('runtime', 'native-experience-init', { platform });

  await Promise.allSettled([
    configureStatusBar(),
    configureSplashScreen(),
  ]);
}

async function configureStatusBar() {
  try {
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setBackgroundColor({ color: '#0A1630' });
  } catch (error) {
    console.warn('StatusBar setup skipped:', error);
  }
}

async function configureSplashScreen() {
  try {
    // Avoid early hide white flash: wait for first frame and short settle.
    const waitForFirstPaint = () =>
      new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const waitForLoadEvent = () =>
      new Promise<void>((resolve) => {
        if (document.readyState === 'complete') {
          resolve();
          return;
        }
        window.addEventListener('load', () => resolve(), { once: true });
      });
    await Promise.race([
      Promise.all([waitForFirstPaint(), waitForLoadEvent()]),
      new Promise<void>((resolve) => window.setTimeout(resolve, 900)),
    ]);
    await SplashScreen.hide({ fadeOutDuration: 180 });
    logNativeEvent('runtime', 'splash-hidden');
  } catch (error) {
    console.warn('SplashScreen hide skipped:', error);
  }
}

function initializeNativeViewportHandling() {
  const applyKeyboardInset = () => {
    const vv = window.visualViewport;
    const offset = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;
    document.documentElement.style.setProperty('--net360-keyboard-offset', `${Math.round(offset)}px`);
  };
  applyKeyboardInset();
  window.visualViewport?.addEventListener('resize', applyKeyboardInset);
  window.visualViewport?.addEventListener('scroll', applyKeyboardInset);
  window.addEventListener('orientationchange', () => {
    window.setTimeout(applyKeyboardInset, 140);
  }, { passive: true });
}

async function registerPushNotificationsIfEnabled() {
  if (!isPushEnabled()) {
    return;
  }

  try {
    const permissionResult = await PushNotifications.requestPermissions();
    if (permissionResult.receive !== 'granted') {
      return;
    }

    await PushNotifications.register();

    PushNotifications.addListener('registration', (token) => {
      // Hook for sending token to backend user profile when endpoint is ready.
      console.info('Push token received:', token.value);
    });

    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.info('Push received in foreground:', notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
      console.info('Push action performed:', event.notification?.data || {});
    });
  } catch (error) {
    console.warn('PushNotifications setup skipped:', error);
  }
}

export async function requestCameraAccessPermission(): Promise<PermissionResolution> {
  if (!isNative()) {
    return {
      granted: true,
      status: 'not-required',
      message: 'Browser mode uses file picker permission flow when you upload an image.',
    };
  }

  try {
    /* Only the camera permission is requested here. Photo/gallery selection uses the
     * system file picker (Android Photo Picker on API 33+), which needs no runtime
     * media permission — see `Profile.tsx`'s `<input type="file">` avatar picker. */
    const result = await Camera.requestPermissions({ permissions: ['camera'] });
    const granted = result.camera === 'granted';
    return {
      granted,
      status: granted ? 'granted' : 'denied',
      message: granted
        ? 'Camera permission granted. You can capture question images.'
        : 'Camera permission denied. You can still use text-based features.',
    };
  } catch {
    return {
      granted: false,
      status: 'error',
      message: 'Camera permission request failed. Try again from device settings.',
    };
  }
}

export async function requestFileAccessPermission(): Promise<PermissionResolution> {
  if (!isNative()) {
    return {
      granted: true,
      status: 'not-required',
      message: 'Browser mode allows selecting documents via file picker when needed.',
    };
  }

  try {
    const result = await Filesystem.requestPermissions();
    const granted = result.publicStorage === 'granted';
    return {
      granted,
      status: granted ? 'granted' : 'denied',
      message: granted
        ? 'File access granted. You can upload PDFs, screenshots, and documents.'
        : 'File access denied. Upload actions may be limited until enabled.',
    };
  } catch {
    return {
      granted: false,
      status: 'error',
      message: 'File access request failed. Try again from device settings.',
    };
  }
}

export async function requestNotificationAccessPermission(): Promise<PermissionResolution> {
  try {
    if (isNative()) {
      const permission = await PushNotifications.requestPermissions();
      const granted = permission.receive === 'granted';
      if (granted) {
        await registerPushNotificationsIfEnabled();
      }
      return {
        granted,
        status: granted ? 'granted' : 'denied',
        message: granted
          ? 'Notification permission granted for reminders and platform updates.'
          : 'Notification permission denied. You can enable it later from settings.',
      };
    }

    if (typeof Notification === 'undefined') {
      return {
        granted: false,
        status: 'not-required',
        message: 'Notification API is unavailable in this browser.',
      };
    }

    const permission = await Notification.requestPermission();
    const granted = permission === 'granted';
    return {
      granted,
      status: granted ? 'granted' : 'denied',
      message: granted
        ? 'Notification permission granted.'
        : 'Notification permission denied. You can still use all core study modules.',
    };
  } catch {
    return {
      granted: false,
      status: 'error',
      message: 'Notification permission request failed. Try again from settings.',
    };
  }
}

export async function hapticTap() {
  if (!isNative()) {
    return;
  }
  await Haptics.impact({ style: ImpactStyle.Light });
}

export async function hapticConfirm() {
  if (!isNative()) {
    return;
  }
  await Haptics.notification({ type: NotificationType.Success });
}

export async function hapticWarning() {
  if (!isNative()) {
    return;
  }
  await Haptics.notification({ type: NotificationType.Warning });
}

export async function hapticError() {
  if (!isNative()) {
    return;
  }
  await Haptics.notification({ type: NotificationType.Error });
}
